"use client";

import { useCallback, useState } from "react";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { SearchBar, MARKETPLACE_SEARCH_PLACEHOLDER_TERMS } from "@/components/features/home/hero/components/SearchBar";
import MarketplaceSearchModal from "./MarketplaceSearchModal";
import type { MarketplaceSearchResult } from "@/types/api/marketplace";

function blurActiveElement() {
  const active = document.activeElement;
  if (active instanceof HTMLElement) active.blur();
}

interface MarketplaceMobileSearchBarProps {
  searchValue: string;
  onSearchChange: (query: string) => void;
  onSearchCommit: (query: string) => void;
  onSearchSuggestionSelect?: (result: MarketplaceSearchResult) => void;
  searchSuggestions: MarketplaceSearchResult[];
  debouncedSearch?: string;
  isSearchFetching?: boolean;
}

/** Full-width mobile search below the marketplace navbar row */
export function MarketplaceMobileSearchBar({
  searchValue,
  onSearchChange,
  onSearchCommit,
  onSearchSuggestionSelect,
  searchSuggestions,
  debouncedSearch = "",
  isSearchFetching = false,
}: MarketplaceMobileSearchBarProps) {
  const [open, setOpen] = useState(false);

  const dismissSearch = useCallback(() => {
    setOpen(false);
    blurActiveElement();
  }, []);

  const handleSearchCommit = useCallback(
    (query: string) => {
      onSearchCommit(query);
      dismissSearch();
    },
    [onSearchCommit, dismissSearch]
  );

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) blurActiveElement();
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <div className="w-full">
          <SearchBar
            searchBarContainerClassName="!mx-0 !h-11 !w-full !max-w-none !shadow-none"
            compact
            searchValue={searchValue}
            setSearchValue={onSearchChange}
            iconSize={18}
            iconContainerClassName="h-9 w-9"
            placeholderTerms={MARKETPLACE_SEARCH_PLACEHOLDER_TERMS}
          />
        </div>
      </DialogTrigger>
      <MarketplaceSearchModal
        searchValue={searchValue}
        setSearchValue={onSearchChange}
        onSearchCommit={handleSearchCommit}
        onSearchSuggestionSelect={onSearchSuggestionSelect}
        onDismiss={dismissSearch}
        searchSuggestions={searchSuggestions}
        debouncedSearch={debouncedSearch}
        isSearchFetching={isSearchFetching}
      />
    </Dialog>
  );
}
